import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useLocation, useNavigate, Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from "./AuthContext";
import { getRateLimitMessage } from "../../utils/rateLimitFeedback";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [errores, setErrores] = useState({});
  const [cargando, setCargando] = useState(false);
  const [recaptchaSize, setRecaptchaSize] = useState("normal");
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  useEffect(() => {
    const actualizarTamanoCaptcha = () => {
      setRecaptchaSize(window.innerWidth < 420 ? "compact" : "normal");
    };

    actualizarTamanoCaptcha();
    window.addEventListener("resize", actualizarTamanoCaptcha);

    return () => window.removeEventListener("resize", actualizarTamanoCaptcha);
  }, []);

  const validar = () => {
    const nuevosErrores = {};
    if (!username.trim()) {
      nuevosErrores.username = "Ingresá tu nombre de usuario.";
    }
    if (!password) {
      nuevosErrores.password = "Ingresá tu contraseña.";
    } else if (password.length < 6) {
      nuevosErrores.password = "La contraseña debe tener al menos 6 caracteres.";
    }
    return nuevosErrores;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    const erroresDetectados = validar();
    if (Object.keys(erroresDetectados).length > 0) {
      setErrores(erroresDetectados);
      return;
    }

    const captchaToken = recaptchaRef.current?.getValue();
    if (!captchaToken) {
      Swal.fire({
        icon: "warning",
        title: "Verificación requerida",
        text: "Por favor completá el reCAPTCHA antes de continuar.",
        confirmButtonColor: "#dc2626",
      });
      return;
    }

    setCargando(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/login`,
        {
          username: username.trim(),
          password,
          recaptcha_token: captchaToken,
        }
      );

      const { access_token, ...datosUsuario } = response.data;
      login(datosUsuario, access_token);

      Swal.fire({
        icon: "success",
        title: `Bienvenido/a, ${datosUsuario.nombre || datosUsuario.username}`,
        showConfirmButton: false,
        timer: 1500,
      });

      if (datosUsuario.rol === "admin") {
        navigate("/dashboard", { replace: true });
      } else {
        const destino = location.state?.from || "/tienda";
        navigate(destino, { replace: true });
      }
    } catch (error) {
      recaptchaRef.current?.reset();
      const status = error.response?.status;

      if (status === 401) {
        setErrores({ password: "Usuario o contraseña incorrectos." });
        Swal.fire({
          icon: "error",
          title: "Login incorrecto",
          text: "El usuario o la contraseña no son correctos.",
          confirmButtonColor: "#111",
        });
      } else if (status === 403) {
        Swal.fire({
          icon: "warning",
          title: "Verificación fallida",
          text: "No pudimos verificar el reCAPTCHA. Intentá de nuevo.",
          confirmButtonColor: "#111",
        });
      } else if (status === 429) {
        Swal.fire({
          icon: "warning",
          title: "Demasiados intentos",
          text: getRateLimitMessage(error),
          confirmButtonColor: "#111",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "No se pudo iniciar sesión. Intentá de nuevo.",
          confirmButtonColor: "#111",
        });
      }
    } finally {
      setCargando(false);
    }
  };

  const limpiarError = (campo) => {
    if (errores[campo]) setErrores({ ...errores, [campo]: null });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-6" style={{ backgroundColor: "#f5f1ed" }}>
      <div className="w-full max-w-sm p-5 sm:p-8 bg-white border border-gray-300">
        <h2 className="text-2xl font-light mb-2 text-center text-gray-900 uppercase tracking-wider">
          Iniciar sesión
        </h2>
        <p className="text-center text-gray-500 text-xs mb-8 uppercase tracking-wider">
          Ingresá a tu cuenta
        </p>

        <form onSubmit={handleLogin} className="space-y-5">
          <div>
            <label className="block text-gray-900 font-medium mb-2 text-xs uppercase tracking-wider">Usuario</label>
            <input
              type="text"
              value={username}
              onChange={(e) => { setUsername(e.target.value); limpiarError("username"); }}
              className={`w-full p-3 border text-sm focus:outline-none focus:ring-1 transition ${
                errores.username
                  ? "border-gray-400 focus:ring-gray-300 bg-white"
                  : "border-gray-300 focus:ring-gray-400"
              }`}
            />
            {errores.username && (
              <p className="text-black text-xs mt-1">{errores.username}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-900 font-medium mb-2 text-xs uppercase tracking-wider">Contraseña</label>
            <div className="relative">
              <input
                type={mostrarPassword ? "text" : "password"}
                value={password}
                onChange={(e) => { setPassword(e.target.value); limpiarError("password"); }}
                className={`w-full border p-3 pr-11 text-sm transition focus:outline-none focus:ring-1 ${
                  errores.password
                    ? "border-gray-400 focus:ring-gray-300 bg-white"
                    : "border-gray-300 focus:ring-gray-400"
                }`}
              />
              <button
                type="button"
                onClick={() => setMostrarPassword((visible) => !visible)}
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-500 transition hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400"
                aria-label={mostrarPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                aria-pressed={mostrarPassword}
                title={mostrarPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {mostrarPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {errores.password && (
              <p className="text-black text-xs mt-1">{errores.password}</p>
            )}
          </div>

          <div className="flex justify-center py-2">
            <div className="recaptcha-responsive">
              <ReCAPTCHA
                key={recaptchaSize}
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                size={recaptchaSize}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={cargando}
            className="w-full bg-black text-white p-3 hover:bg-gray-900 transition font-bold uppercase text-xs tracking-wider disabled:opacity-60"
          >
            {cargando ? "Ingresando..." : "Iniciar sesión"}
          </button>
        </form>

        <p className="text-center text-xs text-gray-600 mt-6 uppercase tracking-wider">
          ¿No tenés cuenta?{" "}
          <Link to="/registro" state={location.state} className="text-gray-900 hover:underline font-medium">
            Registrate
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
